/**
 * Sales by Product Family, drilling Family -> Category -> Product.
 *
 * Everything comes from the order lines' own snapshots (server side). When
 * the period has no Product Family data at all the server says
 * `mode: "category"` and the card becomes Sales by Category -> Product,
 * with a short note instead of an empty or broken chart.
 */
import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Info } from "lucide-react";
import { CANONICAL_FAMILY_ORDER, normalizeFamily } from "../../lib/productFamily";
import { count, familyColor, money, money0, share } from "../../lib/retailReporting";

const SHOW = 7;
const NEUTRAL = "#264073";

function orderFamilies(nodes) {
  const rank = (n) => {
    if (n.key === "") return 1000;
    const i = CANONICAL_FAMILY_ORDER.indexOf(normalizeFamily(n.label));
    return i === -1 ? 100 : i;
  };
  return [...nodes].sort((a, b) => rank(a) - rank(b) || b.net_sales_cents - a.net_sales_cents);
}

function withOther(nodes) {
  if (nodes.length <= SHOW + 1) return nodes;
  const rest = nodes.slice(SHOW);
  return [...nodes.slice(0, SHOW), {
    key: "__other__", label: `Other (${rest.length} more)`, other: true,
    net_sales_cents: rest.reduce((s, n) => s + n.net_sales_cents, 0),
    units: rest.reduce((s, n) => s + (n.units || 0), 0),
  }];
}

export default function FamilyBreakdown({ breakdown }) {
  const [path, setPath] = useState([]);
  const familyMode = breakdown?.mode === "family";
  const rootLabel = familyMode ? "All families" : "All categories";
  const levels = useMemo(() => {
    const out = [breakdown?.nodes || []];
    let nodes = out[0];
    for (const key of path) {
      const n = nodes.find((x) => x.key === key);
      if (!n) break;
      nodes = n.children || [];
      out.push(nodes);
    }
    return out;
  }, [breakdown, path]);
  const depth = levels.length - 1;
  const raw = levels[depth];
  const familyLevel = familyMode && depth === 0;
  const rows = familyLevel ? orderFamilies(raw) : withOther(raw);
  const total = raw.reduce((s, n) => s + n.net_sales_cents, 0);
  const max = Math.max(1, ...rows.map((r) => r.net_sales_cents));
  const leafDepth = familyMode ? 2 : 1;
  const canDrill = depth < leafDepth;
  const familyOf = familyMode && path.length ? (breakdown.nodes.find((n) => n.key === path[0]) || {}) : null;
  const crumbLabels = [];
  {
    let nodes = breakdown?.nodes || [];
    for (const key of path) {
      const n = nodes.find((x) => x.key === key);
      if (!n) break;
      crumbLabels.push(n.label);
      nodes = n.children || [];
    }
  }
  const title = familyMode ? "Sales by product family" : "Sales by category";
  const hint = !raw.length ? "No sales in this period"
    : canDrill ? (familyLevel ? "Click a family to see its categories" : depth === leafDepth - 1 ? "Click a category to see its products" : "")
    : "Products, by Net Sales";

  return (
    <section className="rounded-xl border bg-card shadow-sm min-w-0" aria-labelledby="family-title" data-testid="family-breakdown">
      <div className="px-[18px] pt-4">
        <h2 id="family-title" className="font-display text-[17px] font-semibold">{title}</h2>
        <p className="text-[12.5px] text-muted-foreground">{hint}</p>
      </div>
      <div className="px-[18px] pb-4 pt-3">
        <div className="flex min-h-[30px] flex-wrap items-center gap-2">
          {path.length > 0 && (
            <button type="button" onClick={() => setPath(path.slice(0, -1))} data-testid="family-back"
              className="inline-flex items-center gap-1 rounded-lg border bg-card px-2 py-1 text-[12.5px] hover:bg-secondary">
              <ChevronLeft className="h-3.5 w-3.5" /> Back
            </button>
          )}
          <nav aria-label="Drill-down path" className="flex flex-wrap items-center gap-1 text-[13px] text-muted-foreground">
            {path.length === 0 ? <span className="font-semibold text-foreground">{rootLabel}</span> : (
              <button type="button" className="rounded px-1 hover:bg-secondary hover:text-foreground" onClick={() => setPath([])}>{rootLabel}</button>
            )}
            {crumbLabels.map((l, i) => (
              <React.Fragment key={`${l}-${i}`}>
                <span aria-hidden="true">›</span>
                {i === crumbLabels.length - 1 ? <span className="px-1 font-semibold text-foreground">{l}</span> : (
                  <button type="button" className="rounded px-1 hover:bg-secondary hover:text-foreground" onClick={() => setPath(path.slice(0, i + 1))}>{l}</button>
                )}
              </React.Fragment>
            ))}
          </nav>
        </div>
        {!familyMode && path.length === 0 && raw.length > 0 && (
          <div className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50 px-2.5 py-2 text-[12.5px] text-amber-800" data-testid="family-fallback-note">
            <Info className="mt-0.5 h-3.5 w-3.5 flex-none" />
            <span>No Product Family data for this period, so sales are shown by category.</span>
          </div>
        )}
        <ul className="mt-1.5 flex flex-col gap-0.5" role="list">
          {rows.map((r) => {
            const color = familyMode ? familyColor(familyLevel ? (r.key === "" ? "" : r.label) : familyOf?.label || "") : NEUTRAL;
            const clickable = canDrill && !r.other;
            const body = (
              <>
                <span className="col-start-1 flex min-w-0 items-center gap-1.5 text-[13.5px]">
                  {familyLevel && <i className="h-2.5 w-2.5 flex-none rounded-[3px]" style={{ background: color }} aria-hidden="true" />}
                  <span className="truncate">{r.label}</span>
                  {clickable && <ChevronRight className="h-3.5 w-3.5 flex-none text-muted-foreground" aria-hidden="true" />}
                </span>
                <span className="col-start-2 whitespace-nowrap text-[13px] tabular-nums">
                  {money0(r.net_sales_cents)}<em className="ml-2 inline-block min-w-[34px] text-right not-italic text-muted-foreground">{share(r.net_sales_cents, total)}</em>
                </span>
                <span className="relative col-span-2 h-2.5" aria-hidden="true">
                  <span className="absolute inset-y-0 left-0 rounded-r" style={{ width: `${Math.max(0.5, (r.net_sales_cents / max) * 100)}%`, background: color }} />
                </span>
              </>
            );
            const tip = `${r.label}: ${money(r.net_sales_cents)}, ${count(r.units)} units`;
            return (
              <li key={r.key}>
                {clickable ? (
                  <button type="button" onClick={() => setPath([...path, r.key])} title={tip}
                    aria-label={`${r.label}, ${money(r.net_sales_cents)}. Show ${depth === 0 && familyMode ? "categories" : "products"}`}
                    className="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-x-2.5 gap-y-1 rounded-lg px-2 py-[7px] text-left hover:bg-secondary">
                    {body}
                  </button>
                ) : (
                  <div title={tip} className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-2.5 gap-y-1 px-2 py-[7px]">{body}</div>
                )}
              </li>
            );
          })}
        </ul>
        {path.length > 0 && familyMode && (
          <p className="mt-2.5 text-xs text-muted-foreground">{crumbLabels.join(" › ")} is {share(total, breakdown.nodes.reduce((s, n) => s + n.net_sales_cents, 0))} of Net Sales for the period.</p>
        )}
      </div>
    </section>
  );
}
